import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useGoBack } from '../../hooks/useUrlState';
import Swal from 'sweetalert2';
import api from '../../utils/api';
import RichTextEditor from '../../components/RichTextEditor';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft, faSave, faPaperclip, faInfoCircle, faUsers, faSpinner,
    faClipboardList, faFilm, faFolderOpen, faBuilding, faCalendarDay, faRepeat,
    faMicrophone
} from '@fortawesome/free-solid-svg-icons';
import AttachMenu from '../../components/AttachMenu';
import StagedFiles from '../../components/StagedFiles';
import EmployeeMultiSelect from '../../components/EmployeeMultiSelect';
import VendorSection from '../../components/VendorSection';
import RecentOptions from '../../components/RecentOptions';
import { recordRecentPick } from '../../utils/recentPicks';
import DatePickerField from '../../components/DatePickerField';
import TaskTimeWindow from '../../components/TaskTimeWindow';
import AttachmentRequirement from '../../components/AttachmentRequirement';
import PrivateTaskToggle from '../../components/PrivateTaskToggle';
import ScheduleProjection from '../../components/ScheduleProjection';
import { datesBetween, prettyDate } from '../../utils/scheduleDates';
import UploadProgress from '../../components/UploadProgress';
import '../../styles/App.css';
import '../../styles/tasks.css';
import '../../styles/recurring.css';

const MAX_VIDEO_MB = 300;

const AddTask = () => {
    const navigate = useNavigate();
    const goBack = useGoBack('/tasks');
    // A follow-up from a meeting's record arrives with the form already started
    // (title, notes, project), and "Assign task" in a chat's Tasks panel with the
    // person already picked (assigneeIds). Nobody else ever has this.
    const followUp = useLocation().state?.followUp || null;

    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(null);
    const [isCompressing, setIsCompressing] = useState(false);

    const [projectsList, setProjectsList] = useState([]);
    const [employeesList, setEmployeesList] = useState([]);

    const [formData, setFormData] = useState({
        title: followUp?.title || '',
        description: followUp?.description || '',
        taskType: followUp?.title && !followUp.projectId ? 'Regular Office Task' : 'Project Task',
        projectId: followUp?.projectId || '',
        priority: 'Medium',
        startDate: new Date().toISOString().split('T')[0],
        dueDate: '',
        startTime: '',
        dueTime: '',
        timeAllottedMinutes: '',
        requiresAttachment: false,
        isPrivate: false
    });

    const [selectedAssignees, setSelectedAssignees] = useState(() => followUp?.assigneeIds || []);
    // Vendors on the task (guest role) - one-off tasks only.
    const [selectedVendors, setSelectedVendors] = useState([]);
    const [files, setFiles] = useState([]);

    // --- recurring schedule (TaskPlan.md §13) ---
    // Off by default: a one-off task is still the common case, and the whole
    // form below stays exactly as it was until this is switched on.
    const [isRecurring, setIsRecurring] = useState(false);
    const [plannedDates, setPlannedDates] = useState([]);
    const [visibleRange, setVisibleRange] = useState(null);
    const [annotations, setAnnotations] = useState({});
    const [projection, setProjection] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    useEffect(() => {
        const fetchDropdowns = async () => {
            try {
                const [projRes, empRes] = await Promise.all([
                    api.get('/tasks/assignable-projects'),
                    api.get('/tasks/assignable-employees')
                ]);
                setProjectsList(projRes.data || []);
                setEmployeesList(empRes.data || []);
            } catch (err) {
                console.error('Could not fetch task dropdowns', err);
                Swal.fire('Error', 'Could not load projects or your team list.', 'error');
            }
        };
        fetchDropdowns();
    }, []);

    /**
     * Ask the server what this selection would actually do.
     *
     * One call covers both jobs: annotations for the months on screen (holidays
     * and per-person leave, so the calendar can grey and stripe them) and, once
     * days are picked, the projection that drives the footer.
     *
     * Debounced because it re-runs on every day clicked and every assignee
     * added, and dragging a range fires a burst of those.
     */
    useEffect(() => {
        if (!isRecurring || !visibleRange) return;

        const timer = setTimeout(async () => {
            setPreviewLoading(plannedDates.length > 0 && selectedAssignees.length > 0);
            try {
                const res = await api.post('/tasks/recurring/preview', {
                    from: visibleRange.from,
                    to: visibleRange.to,
                    assigneeIds: selectedAssignees,
                    // Omitted entirely until both are present — the endpoint
                    // rejects a projection with no one to project for.
                    ...(plannedDates.length && selectedAssignees.length ? { dates: plannedDates } : {})
                });
                setAnnotations(res.data.annotations || {});
                setProjection(res.data.projection || null);
            } catch (err) {
                console.error('Could not preview the schedule', err);
                setProjection(null);
            } finally {
                setPreviewLoading(false);
            }
        }, 350);

        return () => clearTimeout(timer);
    }, [isRecurring, visibleRange, plannedDates, selectedAssignees]);

    // Stable identity so the calendar's effect doesn't refire every render.
    const handleVisibleRange = useCallback((from, to) => {
        setVisibleRange(prev => (prev?.from === from && prev?.to === to ? prev : { from, to }));
    }, []);

    // A one-off task's start..due is just a contiguous range, so it drives the
    // same picker the recurring day set uses — one calendar, two modes.
    const oneOffDates = formData.dueDate
        ? datesBetween(formData.startDate || formData.dueDate, formData.dueDate)
        : (formData.startDate ? [formData.startDate] : []);

    const handleOneOffDates = (dates) => {
        setFormData(prev => ({
            ...prev,
            startDate: dates[0] || '',
            dueDate: dates.length ? dates[dates.length - 1] : ''
        }));
    };

    const dateFieldLabel = () => {
        if (isRecurring) {
            if (plannedDates.length === 0) return 'Pick the days this runs on';
            return `${prettyDate(plannedDates[0])} → ${prettyDate(plannedDates[plannedDates.length - 1])}`;
        }
        if (!formData.dueDate) return 'Pick the start and due date';
        if (formData.startDate === formData.dueDate) return prettyDate(formData.dueDate);
        return `${prettyDate(formData.startDate)} → ${prettyDate(formData.dueDate)}`;
    };

    // One line under the field, so the skip detail is visible without
    // reopening the popup. The full breakdown lives inside it.
    const projectionHeadline = () => {
        if (!isRecurring || !projection) return null;
        const skipped = projection.perUser.reduce(
            (max, u) => Math.max(max, u.timeline.filter(t => !t.willRun && t.date).length), 0);
        const bits = [`${projection.targetCount} task${projection.targetCount === 1 ? '' : 's'} each`];
        if (skipped > 0) bits.push(`${skipped} day${skipped === 1 ? '' : 's'} skipped, rolled forward`);
        if (projection.summary?.endsOn) bits.push(`ends ${prettyDate(projection.summary.endsOn)}`);
        return bits.join(' · ');
    };

    const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

    // AttachMenu has already filtered by type, enforced the size cap and
    // compressed any images, so this is only where they land.
    const addFiles = (picked) => setFiles(prev => [...prev, ...picked]);

    const removeFile = (index) => setFiles(prev => prev.filter((_, i) => i !== index));

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (formData.taskType === 'Project Task' && !formData.projectId) {
            return Swal.fire('Pick a Project', 'Choose a project, or switch this to a Regular Office task.', 'warning');
        }
        const vendorCount = isRecurring ? 0 : selectedVendors.length;
        if (selectedAssignees.length === 0 && vendorCount === 0) {
            return Swal.fire('No Assignees', isRecurring
                ? 'Select at least one employee to assign this task to.'
                : 'Select at least one employee or guest to assign this task to.', 'warning');
        }
        if (isRecurring && plannedDates.length === 0) {
            return Swal.fire('Pick Some Days', 'Choose at least one day on the calendar for this task to run.', 'warning');
        }
        // The date field is a button now, so the browser can't enforce this.
        if (!isRecurring && !formData.dueDate) {
            return Swal.fire('Pick a Due Date', 'Choose when this task is due.', 'warning');
        }
        if (!isRecurring && formData.startDate && formData.dueDate && new Date(formData.dueDate) < new Date(formData.startDate)) {
            return Swal.fire('Check Dates', 'The due date cannot be before the start date.', 'warning');
        }
        if (formData.startTime && formData.dueTime && formData.dueTime <= formData.startTime) {
            return Swal.fire('Check Times', 'The due/end time must be after the start time.', 'warning');
        }

        setLoading(true);
        setUploadProgress(null);

        try {
            const data = new FormData();
            files.forEach(f => data.append('attachments', f));
            data.append('assigneeIds', JSON.stringify(selectedAssignees));
            if (!isRecurring) data.append('externalAssigneeIds', JSON.stringify(selectedVendors));

            if (isRecurring) {
                // A schedule has no single due date — each generated task gets
                // its own, on the day it lands. The time window travels with
                // the brief though, same as title/priority: it's copied onto
                // every day this schedule generates.
                ['title', 'description', 'taskType', 'projectId', 'priority', 'startTime', 'dueTime', 'timeAllottedMinutes', 'requiresAttachment']
                    .forEach(key => data.append(key, formData[key]));
                data.append('plannedDates', JSON.stringify(plannedDates));
            } else {
                Object.keys(formData).forEach(key => data.append(key, formData[key]));
            }

            const res = await api.post(isRecurring ? '/tasks/recurring' : '/tasks', data, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (progressEvent) => {
                    // No total (chunked or blocked by a proxy) stays indeterminate rather than showing NaN%.
                    if (!files.length || !progressEvent.total) return;
                    setUploadProgress(Math.min(100, Math.round((progressEvent.loaded * 100) / progressEvent.total)));
                }
            });

            const hasVideo = files.some(f => f.type.startsWith('video/'));
            const videoNote = hasVideo
                ? ' Videos are viewable right away and will be optimised overnight.'
                : '';

            if (isRecurring) {
                const startedToday = res.data?.generatedToday > 0;
                await Swal.fire({
                    icon: 'success',
                    title: 'Daily Task Scheduled',
                    text: `${plannedDates.length} day${plannedDates.length === 1 ? '' : 's'} scheduled for `
                        + `${selectedAssignees.length} ${selectedAssignees.length === 1 ? 'person' : 'people'}. `
                        + (startedToday
                            ? "Today's task has already gone out."
                            : 'The first one goes out at 6am on the first scheduled day.')
                        + videoNote,
                    timer: hasVideo ? 4500 : 3200,
                    showConfirmButton: false
                });
                navigate('/tasks?view=recurring');
            } else {
                await Swal.fire({
                    icon: 'success',
                    title: 'Task Assigned',
                    text: 'Your team has been notified.' + videoNote,
                    timer: hasVideo ? 3500 : 1800,
                    showConfirmButton: false
                });
                goBack();
            }
        } catch (err) {
            console.error('Task creation error:', err);
            const message = err.response?.data?.message
                || (err.response?.status === 413
                    ? `File too large. Videos must be under ${MAX_VIDEO_MB}MB.`
                    : 'Failed to create the task. Please try again.');
            Swal.fire(isRecurring ? 'Could Not Schedule Task' : 'Could Not Assign Task', message, 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="attendance-container fade-in">
            <div className="task-page-header">
                <button className="gts-btn secondary" onClick={() => goBack()}>
                    <FontAwesomeIcon icon={faArrowLeft} /> Back
                </button>
                <h1 className="page-title header-no-margin">
                    <FontAwesomeIcon icon={faClipboardList} style={{ marginRight: '10px', color: 'light-dark(#215D7B, var(--theme-accent-ink))' }} />
                    {isRecurring ? 'Schedule a Daily Task' : 'Assign New Task'}
                </h1>
                <button
                    type="button"
                    className="gts-btn secondary"
                    onClick={() => navigate('/add-task/voice')}
                    style={{ marginLeft: 'auto' }}
                >
                    <FontAwesomeIcon icon={faMicrophone} /> Voice Assign
                </button>
            </div>

            <form onSubmit={handleSubmit}>
                <div className="task-form-grid">

                    {/* ---------- LEFT: what the task is ---------- */}
                    <section className="task-form-card">
                        <div className="expense-section-title">
                            <FontAwesomeIcon icon={faInfoCircle} /> Task Details
                        </div>

                        {/* Type comes first — it decides whether a project is even asked for. */}
                        <div className="form-group task-field">
                            <label className="input-label">Task Type</label>
                            <div className="type-toggle">
                                <button
                                    type="button"
                                    className={`type-toggle-btn ${formData.taskType === 'Project Task' ? 'active' : ''}`}
                                    onClick={() => setFormData(prev => ({ ...prev, taskType: 'Project Task' }))}
                                >
                                    <FontAwesomeIcon icon={faFolderOpen} /> Project Task
                                </button>
                                <button
                                    type="button"
                                    className={`type-toggle-btn ${formData.taskType === 'Regular Office Task' ? 'active' : ''}`}
                                    onClick={() => setFormData(prev => ({ ...prev, taskType: 'Regular Office Task', projectId: '' }))}
                                >
                                    <FontAwesomeIcon icon={faBuilding} /> Regular Office Task
                                </button>
                            </div>
                            <small className="task-hint">
                                {formData.taskType === 'Regular Office Task'
                                    ? 'Internal work not tied to any client project — no project needed.'
                                    : 'Work that belongs to a specific project.'}
                            </small>
                        </div>

                        <div className="form-group task-field">
                            <label className="input-label">Task Title *</label>
                            <input
                                type="text" name="title" className="custom-input"
                                value={formData.title} onChange={handleChange}
                                placeholder="e.g. Shoot 20 product clips for the Spectra launch"
                                required
                            />
                        </div>

                        <div className="form-group task-field field-grow">
                            <label className="input-label">Description</label>
                            <RichTextEditor
                                name="description" rows={8}
                                value={formData.description}
                                onChange={(v) => setFormData((f) => ({ ...f, description: v }))}
                                placeholder="What exactly needs to be done? Include any references or acceptance criteria."
                            />
                        </div>

                        {/* Project stays next to the toggle that decides whether it's asked for. */}
                        {formData.taskType === 'Project Task' && (
                            <div className="form-group task-field">
                                <label className="input-label">Project *</label>
                                <select name="projectId" className="swal2-select custom-select" value={formData.projectId} onChange={(e) => { handleChange(e); recordRecentPick('project', e.target.value); }} required>
                                    <option value="">Select a project</option>
                                    <RecentOptions kind="project" items={projectsList} />
                                </select>
                                {projectsList.length === 0 && (
                                    <small className="task-hint">No active projects found. Ask an admin to create one first.</small>
                                )}
                            </div>
                        )}
                    </section>

                    {/* ---------- RIGHT: when it's due, who it goes to, what comes with it ---------- */}
                    <div className="task-form-col">
                        <section className="task-form-card">
                            <div className="expense-section-title">
                                <FontAwesomeIcon icon={faCalendarDay} /> Priority &amp; Schedule
                            </div>

                            {/* The switch that turns one task into a run of them. */}
                            <div className={`rt-switch-row ${isRecurring ? 'is-on' : ''}`}>
                                <button
                                    type="button"
                                    className={`rt-switch ${isRecurring ? 'is-on' : ''}`}
                                    onClick={() => setIsRecurring(v => !v)}
                                    aria-pressed={isRecurring}
                                    aria-label="Repeat this task daily"
                                />
                                <div className="rt-switch-text">
                                    <strong>
                                        <FontAwesomeIcon icon={faRepeat} style={{ marginRight: '6px', color: 'light-dark(#215D7B, var(--theme-accent-ink))' }} />
                                        Repeat daily
                                    </strong>
                                    <small>
                                        {isRecurring
                                            ? 'A fresh copy goes out each morning. Sundays, holidays and approved leave are skipped, and the run rolls forward so the count still adds up.'
                                            : 'Assign the same task every day for a stretch of days — a daily post, a daily stock check.'}
                                    </small>
                                </div>
                            </div>

                            {/* Two columns, identical in both modes, so nothing
                                jumps around when the switch is flipped. */}
                            <div className="task-field-row cols-2">
                                <div className="form-group task-field">
                                    <label className="input-label">Priority</label>
                                    <select name="priority" className="swal2-select custom-select" value={formData.priority} onChange={handleChange}>
                                        <option value="Low">Low</option>
                                        <option value="Medium">Medium</option>
                                        <option value="High">High</option>
                                        <option value="Urgent">Urgent</option>
                                    </select>
                                </div>

                                <div className="form-group task-field">
                                    <label className="input-label">
                                        {isRecurring ? 'Which days? *' : 'Start & due date *'}
                                    </label>
                                    <DatePickerField
                                        displayValue={dateFieldLabel()}
                                        isEmpty={isRecurring ? plannedDates.length === 0 : !formData.dueDate}
                                        countBadge={isRecurring && plannedDates.length > 0
                                            ? `${plannedDates.length} days` : null}
                                        mode={isRecurring ? 'multi' : 'range'}
                                        selected={isRecurring ? plannedDates : oneOffDates}
                                        onChange={isRecurring ? setPlannedDates : handleOneOffDates}
                                        annotations={isRecurring ? annotations : {}}
                                        onVisibleRangeChange={isRecurring ? handleVisibleRange : undefined}
                                    />
                                    {isRecurring && projectionHeadline() && (
                                        <small className="task-hint">{projectionHeadline()}</small>
                                    )}
                                </div>
                            </div>

                            <div className="form-group task-field">
                                <TaskTimeWindow
                                    startTime={formData.startTime}
                                    dueTime={formData.dueTime}
                                    timeAllottedMinutes={formData.timeAllottedMinutes}
                                    onChange={(patch) => setFormData(prev => ({ ...prev, ...patch }))}
                                    {...(isRecurring ? { fallbackLabel: 'that day ends' } : {})}
                                />
                            </div>

                            <div className="form-group task-field">
                                <AttachmentRequirement
                                    value={formData.requiresAttachment}
                                    onChange={(v) => setFormData(prev => ({ ...prev, requiresAttachment: v }))}
                                    noun={isRecurring ? 'each day of this task' : 'this task'}
                                />
                            </div>

                            {/* One-off tasks only — recurring schedules don't
                                support privacy. */}
                            {!isRecurring && (
                                <div className="form-group task-field">
                                    <PrivateTaskToggle
                                        value={formData.isPrivate}
                                        onChange={(v) => setFormData(prev => ({ ...prev, isPrivate: v }))}
                                    />
                                </div>
                            )}

                            {/* The full skip breakdown sits in the form, not in the
                                popover — it can run to several lines, and keeping it
                                here is what lets the popover stay small. */}
                            {isRecurring && plannedDates.length > 0 && (
                                <ScheduleProjection
                                    projection={projection}
                                    loading={previewLoading}
                                    selectedCount={plannedDates.length}
                                />
                            )}
                        </section>

                        <section className="task-form-card">
                            <div className="expense-section-title">
                                <FontAwesomeIcon icon={faUsers} /> Assign To
                                {selectedAssignees.length > 0 && (
                                    <span className="task-count-pill">{selectedAssignees.length} selected</span>
                                )}
                            </div>

                            <EmployeeMultiSelect
                                employees={employeesList}
                                selected={selectedAssignees}
                                onChange={setSelectedAssignees}
                                recentKey="task-assignee"
                                emptyText="No employees are mapped to you as their Team Lead yet — ask HR to set that up."
                            />

                            {employeesList.length > 0 && (
                                <small className="task-hint">
                                    Search by name or employee ID, then tick everyone who should own this task.
                                </small>
                            )}
                        </section>

                        {/* Vendors (guest role). Not on a schedule: recurring
                            work is generated for employees only. */}
                        {!isRecurring && (
                            <VendorSection selected={selectedVendors} onChange={setSelectedVendors} />
                        )}

                        <section className="task-form-card">
                            <div className="expense-section-title">
                                <FontAwesomeIcon icon={faPaperclip} /> Reference Images, Videos &amp; Documents
                            </div>

                            <AttachMenu
                                recordingKey="task:new"
                                recordingTitle="Task assignment"
                                label="Add attachment"
                                onFiles={addFiles}
                                onBusyChange={setIsCompressing}
                                maxFileMB={MAX_VIDEO_MB}
                            />

                            <div className="task-upload-note">
                                <FontAwesomeIcon icon={faFilm} />
                                <span>
                                    Videos up to {MAX_VIDEO_MB}MB upload as-is and are watchable immediately.
                                    They&apos;re compressed and moved to permanent storage automatically at midnight.
                                </span>
                            </div>

                            {isCompressing && (
                                <p className="file-success-text" style={{ color: 'light-dark(#d97706, var(--theme-warning-ink))', marginTop: '8px', fontWeight: '600' }}>
                                    <FontAwesomeIcon icon={faSpinner} spin /> Preparing files...
                                </p>
                            )}

                            <StagedFiles files={files} onRemove={removeFile} />
                        </section>
                    </div>
                </div>

                {/* Sticky so the form never has to be scrolled back to reach Submit. */}
                <div className="task-form-footer">
                    {loading && (
                        <UploadProgress
                            progress={uploadProgress}
                            count={files.length}
                            savingLabel={files.length ? 'Saving to storage…' : (isRecurring ? 'Scheduling task…' : 'Assigning task…')}
                        />
                    )}
                    <div className="task-form-footer-actions">
                        <span className="task-form-summary">
                            {selectedAssignees.length > 0
                                ? `${selectedAssignees.length} assignee${selectedAssignees.length > 1 ? 's' : ''}`
                                : 'No assignees yet'}
                            {!isRecurring && selectedVendors.length > 0 &&
                                ` · ${selectedVendors.length} guest${selectedVendors.length > 1 ? 's' : ''}`}
                            {files.length > 0 && ` · ${files.length} attachment${files.length > 1 ? 's' : ''}`}
                            {isRecurring && plannedDates.length > 0 &&
                                ` · ${plannedDates.length} day${plannedDates.length > 1 ? 's' : ''} scheduled`}
                        </span>
                        <button type="submit" className="gts-btn primary" disabled={loading || isCompressing}>
                            {loading
                                ? <><FontAwesomeIcon icon={faSpinner} spin /> {isRecurring ? 'Scheduling...' : 'Assigning...'}</>
                                : isRecurring
                                    ? <><FontAwesomeIcon icon={faRepeat} /> Schedule Daily Task</>
                                    : <><FontAwesomeIcon icon={faSave} /> Assign Task</>}
                        </button>
                    </div>
                </div>
            </form>

        </div>
    );
};

export default AddTask;
